import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Search, RefreshCw, Send, ChevronDown, ChevronRight, Loader2, ExternalLink, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/formatters';
import { STAGE_META, filterRows, rowActions, tilesFromNeeds } from '../../components/syndication/needsSyndicationModel';
import { useOfferSend, SendOfferDialog, InviteeTable, when } from '../../components/syndication/offerSendKit';
import AddSyndicatorDialog from '../../components/syndication/AddSyndicatorDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Needs Syndication — 10/27 makeover (Josh 10/23: "deals that still need syndication … send out the emails for
 * confirmation of syndication, and ability to resend them"). One list of every active deal that is not fully placed
 * (or still has an offer out): tiles up top double as filters; every row shows placed / PFS / available %, the offer
 * in flight, and sends / resends the offer email right here (same link per syndicator on a resend).
 */
export default function UnsyndicatedDeals() {
  const { getAuthHeaders } = useAuth();
  const headers = useCallback(() => ({ ...getAuthHeaders(), 'X-Tenant': 'live' }), [getAuthHeaders]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [stage, setStage] = useState('all');
  const [open, setOpen] = useState({});
  const [addFor, setAddFor] = useState(null);   // 10/28: add a syndicator on the fly (no offer email)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/syndications-outstanding/needs`, { headers: headers() });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the list');
    } finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const { busyKey, send, openSendDialog, resendUnanswered, dialogProps } = useOfferSend({ onDone: load });
  const rows = useMemo(() => filterRows(data?.rows, { stage, q }), [data, stage, q]);
  const tiles = tilesFromNeeds(data?.tiles);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Needs syndication</h1>
            <p className="text-sm text-gray-500">Every active deal that is not fully placed. Send the offer email from the row; a resend keeps each syndicator&apos;s personal link.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </div>

        {/* tiles = filters (Josh: "in boxes, just like the home page") */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="needs-tiles">
          {tiles.map((t) => {
            const accent = t.good ? 'text-emerald-700' : t.warn ? 'text-red-600' : 'text-slate-900';
            const active = stage === t.key;
            return (
              <button key={t.key} type="button" onClick={() => setStage(t.key)} data-testid={`needs-tile-${t.key}`}
                className={`text-left rounded-lg border bg-white p-3 h-full transition ${active ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-blue-400 hover:shadow-sm'}`}>
                <p className={`text-2xl font-bold ${accent}`}>{t.value}</p>
                <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">{t.label}</p>
                {t.sub && <p className="text-xs text-slate-400 mt-0.5">{t.sub}</p>}
              </button>
            );
          })}
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b">
              <div className="relative w-full max-w-sm">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search merchant or deal…" className="pl-8" />
              </div>
              {stage !== 'all' && <button type="button" className="text-xs text-blue-700 underline" onClick={() => setStage('all')}>show all</button>}
              <span className="ml-auto text-xs text-slate-500">{rows.length} deal{rows.length === 1 ? '' : 's'}</span>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Deal</TableHead>
                    <TableHead>Merchant</TableHead>
                    <TableHead>Funded</TableHead>
                    <TableHead className="text-right">Funding</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead>Placed</TableHead>
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Offer</TableHead>
                    <TableHead className="text-right w-[26rem]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && !data ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-10 text-gray-400">{stage === 'all' && !q ? 'Every active deal is fully placed. 🎉' : 'Nothing matches.'}</TableCell></TableRow>
                  ) : rows.map((r) => {
                    const a = rowActions(r);
                    const o = r.offer;
                    const meta = STAGE_META[r.stage] || {};
                    const busy = busyKey === r.deal_id;
                    const placed = Math.min(100, (r.syndicated_pct || 0) + (r.pfs_pct || 0));
                    return (
                      <React.Fragment key={r.deal_id}>
                        <TableRow className={r.stage === 'ready_to_confirm' ? 'bg-emerald-50/40' : o?.pending_adjustments ? 'bg-amber-50/40' : ''}>
                          <TableCell>
                            {o && <button type="button" className="text-slate-500" aria-label="toggle invitees" onClick={() => setOpen((s) => ({ ...s, [r.deal_id]: !s[r.deal_id] }))}>
                              {open[r.deal_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>}
                          </TableCell>
                          <TableCell className="font-medium"><Link to={`/deals/${r.deal_id}`} className="text-blue-600 hover:underline">#{r.deal_id_number}</Link><div className="text-xs text-slate-500 max-w-[180px] truncate">{r.deal_name}</div></TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate">{r.merchant || '—'}</TableCell>
                          <TableCell className="text-xs text-slate-500">{r.funding_date || '—'}<div><Badge variant="outline" className="text-[10px] font-normal text-gray-600">{r.status}</Badge></div></TableCell>
                          <TableCell className="text-right text-sm">{formatCurrency(r.funding_amount)}</TableCell>
                          <TableCell className="text-right text-sm">{r.has_commission ? formatCurrency(r.broker_commission) : <Link to={`/deals/${r.deal_id}`} className="text-xs text-red-600 hover:underline">attach first</Link>}</TableCell>
                          <TableCell className="min-w-[150px]">
                            <div className="h-2 rounded bg-slate-100 overflow-hidden flex" title={`syndicators ${r.syndicated_pct}% · PFS ${r.pfs_pct}%`}>
                              <div className="bg-blue-500 h-full" style={{ width: `${Math.min(100, r.syndicated_pct || 0)}%` }} />
                              <div className="bg-slate-400 h-full" style={{ width: `${Math.min(100 - Math.min(100, r.syndicated_pct || 0), r.pfs_pct || 0)}%` }} />
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">{placed}% placed{r.pfs_pct ? ` (PFS ${r.pfs_pct}%)` : ''}{r.positions ? ` · ${r.positions} position${r.positions === 1 ? '' : 's'}` : ''}</div>
                          </TableCell>
                          <TableCell className="text-right font-semibold">{r.available_pct}%</TableCell>
                          <TableCell><span className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${meta.chip || ''}`} title={meta.hint}>{meta.label || r.stage}</span></TableCell>
                          <TableCell className="text-xs text-slate-600">
                            {o ? (<>
                              <div>{o.invited} invited · {o.answered} answered{o.unanswered ? <span className="text-blue-700 font-semibold"> · {o.unanswered} waiting</span> : null}{o.pending_adjustments ? <span className="text-amber-700 font-semibold"> · {o.pending_adjustments} to approve</span> : null}</div>
                              <div className="text-slate-400">{o.never_emailed ? <span className="text-red-600">{o.never_emailed} never emailed · </span> : null}last {when(o.offer_updated_at)}</div>
                            </>) : <span className="text-slate-400">no offer out</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1 flex-wrap">
                              <Button size="sm" variant="outline" disabled={busy || r.available_pct <= 0} title={r.available_pct > 0 ? 'Add a position now, without an offer email' : 'Nothing left to place'} onClick={() => setAddFor(r)}><UserPlus className="h-3.5 w-3.5 mr-1" /> Add syndicator</Button>
                              <Button size="sm" variant={o ? 'outline' : 'default'} disabled={busy || !a.canSend} title={a.canSend ? 'Pick syndicators and email the offer' : 'Attach the broker commission first'} onClick={() => openSendDialog({ ...r, invitations: o?.invitations || [] })}><Send className="h-3.5 w-3.5 mr-1" /> Send to…</Button>
                              <Button size="sm" variant="outline" disabled={busy || !a.canResend} title="Email again everyone who has not answered" onClick={() => resendUnanswered({ ...r, invitations: o?.invitations || [] })}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Resend{o?.unanswered ? ` (${o.unanswered})` : ''}</Button>
                              {a.review && <Link to={`/deals/${r.deal_id}/offering`}><Button size="sm">Review</Button></Link>}
                              {a.confirm && <Link to={`/deals/${r.deal_id}/offering`}><Button size="sm">Confirm</Button></Link>}
                              {!a.review && !a.confirm && <Link to={`/deals/${r.deal_id}/offering`} title="Offering page"><Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /></Button></Link>}
                            </div>
                          </TableCell>
                        </TableRow>
                        {o && open[r.deal_id] && (
                          <TableRow><TableCell colSpan={11} className="bg-slate-50 p-0"><InviteeTable row={{ ...r, invitations: o.invitations || [] }} busyKey={busyKey} send={send} /></TableCell></TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
      <SendOfferDialog {...dialogProps} />
      <AddSyndicatorDialog row={addFor} onClose={() => setAddFor(null)} onDone={load} />
    </Layout>
  );
}
